import styled, { css } from 'styled-components';

import type { Size } from '../../types';
import { convertHexToRgba, getFontStyle, rem, toRemString } from '../../utils';
import type { InputExtensionType } from './Input.type';

const cssInputSize = css<{
  $inputSize: Size;
}>`
  ${({ $inputSize, theme }) => {
    const definedSize = theme.Form.InputSize[$inputSize] ?? theme.Form.InputSize.Medium;

    const width = $inputSize === 'Small' ? rem(definedSize.width) : '100%';
    const height = rem(definedSize.height);

    return css`
      width: ${width};
      height: ${height};
    `;
  }}
`;

export const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledReadOnly = css`
  cursor: default;
  user-select: none;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};

  &:hover {
    border-color: ${({ theme }) => theme.Color.Neutrals.Border};
    transition: border-color ${({ theme }) => theme.Transition.Timing.In};
  }

  &:focus-within {
    box-shadow: none;
    border-color: ${({ theme }) => theme.Color.Neutrals.Border};
    transition: all ${({ theme }) => theme.Transition.Timing.In};
  }
`;

export const StyledDisabled = css`
  cursor: not-allowed;
  user-select: none;
  background-color: ${({ theme }) => theme.Color.Neutrals.Light};
  transition: all ${({ theme }) => theme.Transition.Timing.Out};

  &:hover {
    border-color: ${({ theme }) => theme.Color.Neutrals.Border};
    transition: border-color ${({ theme }) => theme.Transition.Timing.In};
  }

  &:focus-within {
    box-shadow: none;
    border-color: ${({ theme }) => theme.Color.Neutrals.Border};
    transition: all ${({ theme }) => theme.Transition.Timing.In};
  }
`;

export const StyledInputWrapper = styled.span<{
  $inputSize: Size;
  $isError?: boolean;
  $type?: string;
  $variant?: 'default' | 'transparent';
  $readOnly?: boolean;
  $disabled?: boolean;
}>`
  position: relative;
  display: inline-flex;
  max-width: 100%;
  ${cssInputSize}
  box-sizing: border-box;
  background: ${({ theme, $variant }) =>
    $variant !== 'transparent' ? theme.Components.Panel.Background : 'transparent'};
  border: ${({ theme, $isError }) =>
    `${rem(theme.Border.Size.Small)} solid ${
      !$isError ? theme.Color.Neutrals.Border : theme.Color.Feedback.Alert
    }`};
  border-radius: ${({ theme, $type }) =>
    $type === 'number' ? rem(theme.Form.Input.Number.Radius) : rem(theme.Form.Input.Radius)};
  color: ${({ theme }) => theme.Color.Neutrals.Dark};
  outline: none;
  max-width: 100%;
  transition: ${({ theme }) =>
    `box-shadow ${theme.Transition.Timing.Out}, border-color ${theme.Transition.Timing.Out}`};

  &::placeholder {
    color: ${({ theme }) => theme.Form.Input.Placeholder.Color};
  }

  &:focus-within {
    box-shadow: ${({ theme, $isError }) =>
      `${toRemString([0, 0, 0, theme.Border.Size.Medium])} ${
        !$isError
          ? convertHexToRgba(theme.Color.Brand.Primary, 50)
          : convertHexToRgba(theme.Color.Feedback.Alert, 50)
      }`};
    border-color: ${({ theme, $isError }) =>
      !$isError ? theme.Color.Brand.Primary : theme.Color.Feedback.Alert};
    transition: ${({ theme }) =>
      `box-shadow ${theme.Transition.Timing.In}, border-color ${theme.Transition.Timing.In}`};
  }

  &:hover {
    border-color: ${({ theme, $isError }) =>
      !$isError ? theme.Color.Brand.Primary : theme.Color.Feedback.Alert};
    transition: border-color ${({ theme }) => theme.Transition.Timing.In};
  }

  ${({ $readOnly }) => $readOnly && StyledReadOnly}
  ${({ $disabled }) => $disabled && StyledDisabled}

  align-items: center;
`;

export const StyledLabel = styled.label`
  display: block;
  padding: 0 0 ${({ theme }) => rem(theme.Spacing.Size.XSmall)} 0;
`;

export const StyledLabelRequired = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.Color.Feedback.Alert};
  white-space: pre-wrap;
`;

export const StyledError = styled.div`
  margin-top: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
`;

export const StyledInputNumber = css`
  appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    display: none;
  }
`;

export const StyledInput = styled.input<{
  $inputSize: Size;
  $extensions: Array<InputExtensionType>;
}>`
  && {
    ${({ $inputSize, theme, $extensions }) => {
      const extraPadding = getExtraPadding(theme, $extensions);

      return css`
        color: ${({ theme }) => theme.Color.Neutrals.Dark};
        ${getFontStyle($inputSize === 'Small' ? 'Small' : 'Body')}
        padding: ${$inputSize === 'Small'
          ? toRemString([
              theme.Spacing.Size.XXSmall,
              extraPadding || theme.Spacing.Size.XSmall,
              theme.Spacing.Size.XXSmall,
              theme.Spacing.Size.XSmall,
            ])
          : toRemString([
              theme.Spacing.Size.XSmall,
              extraPadding || theme.Spacing.Size.XSmall,
              theme.Spacing.Size.XSmall,
              theme.Spacing.Size.XSmall,
            ])};
      `;
    }}

    &[readonly] {
      cursor: default;
      user-select: none;
    }

    &:disabled {
      cursor: not-allowed;
      user-select: none;
    }
  }

  &[type='number'] {
    ${StyledInputNumber}
    text-align: left;

    ${({ theme }) => css`
      min-width: ${rem(theme.Form.InputSize.Small.width)};
    `}
  }

  border: none;
  background: transparent;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

export const StyledFixedButton = styled.div`
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  align-items: center;
`;

const getExtraPadding = (theme: any, extensions: Array<InputExtensionType>) => {
  if (extensions.length === 0) {
    return 0;
  } else if (extensions.includes('number-controller')) {
    return theme.Form.InputNumberControllers.Small.width;
  } else if (extensions.includes('addons-after')) {
    return theme.Form.InputAddons.width;
  }
};

export const StyledCounter = styled.div`
  display: flex;
  padding-right: ${({ theme }) => rem(theme.Spacing.Size.Small)};
`;
